// src/pages/Administracion.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    FaBuilding,
    FaUsers,
    FaCreditCard,
    FaQuestionCircle,
    FaFileInvoiceDollar,
    FaBullhorn,
    FaChartLine,
    FaEye,
    FaCalendarAlt,
    FaHandshake,
    FaIdCard,
    FaShieldAlt,
    FaBan,
    FaExclamationTriangle,
    FaGavel,
    FaFileAlt,
    FaDonate,
    FaTint
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, onSnapshot, where, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import './Administracion.css';
import { useModulos } from '../hooks/useModulos';

const Administracion = () => {
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd;
    const { modulos, loading: loadingModulos } = useModulos(fraccionamientoId);

    const [metrics, setMetrics] = useState({
        totalResidentes: 0,
        tasaCobro: 0,
        pagosPendientes: 0,
        visitasHoy: 0,
        eventosProximos: 0,
    });

    const [loadingMetrics, setLoadingMetrics] = useState(true);
    const [multasConfig, setMultasConfig] = useState({ enabled: false, list: [] });

    // Cargar métricas
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoadingMetrics(false);
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayString = today.toISOString().split('T')[0];
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowTimestamp = Timestamp.fromDate(tomorrow);

        // Total de residentes (casas sin contar caseta) y tasa de cobro (casas al corriente)
        const casasUnsubscribe = onSnapshot(
            collection(db, 'fraccionamientos', fraccionamientoId, 'casas'),
            (snapshot) => {
                let totalCasas = 0;
                let casasAlCorriente = 0;

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    const rolCasa = (data.rol || data.rolAsignado || '').toLowerCase();
                    const perfilCasa = (data.perfil || '').toLowerCase();
                    const esCaseta = data.esRolCaseta === true || rolCasa === 'caseta' || perfilCasa === 'caseta';

                    // Solo contar casas que NO son caseta
                    if (!esCaseta) {
                        totalCasas++;

                        // Verificar si está al corriente (sin adeudo)
                        const adeudo = Number(data.adeudo ?? 0);
                        const adeudoCapital = Number(data.adeudoCapital ?? 0);
                        const adeudoIntereses = Number(data.adeudoIntereses ?? 0);
                        const totalAdeudo = adeudo > 0 ? adeudo : (adeudoCapital + adeudoIntereses);

                        if (totalAdeudo <= 0) {
                            casasAlCorriente++;
                        }
                    }
                });

                // Calcular tasa de cobro: casas al corriente / total casas (sin caseta)
                const tasaCobro = totalCasas > 0
                    ? Math.round((casasAlCorriente / totalCasas) * 100)
                    : 0;

                setMetrics((prev) => ({
                    ...prev,
                    totalResidentes: totalCasas,
                    tasaCobro,
                }));
            }
        );

        // Pagos pendientes (solo los que NO están pagados en ingresosPendientes)
        const ingresosUnsubscribe = onSnapshot(
            collection(db, 'fraccionamientos', fraccionamientoId, 'ingresosPendientes'),
            async (snapshot) => {
                let pagosPendientesCount = 0;

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    const estado = String(data.estado || '').toLowerCase();

                    // Solo contar los que NO están pagados
                    if (estado !== 'pagado' && estado !== 'liquidado') {
                        pagosPendientesCount++;
                    }
                });

                setMetrics((prev) => ({
                    ...prev,
                    pagosPendientes: pagosPendientesCount,
                }));
            }
        );

        // Visitas actualmente dentro del fraccionamiento (sin importar la fecha)
        const visitasUnsubscribe = onSnapshot(
            collection(db, 'fraccionamientos', fraccionamientoId, 'accesosVisitantes'),
            (snapshot) => {
                let visitasEnSitioCount = 0;

                console.log('🔍 [Administracion] Verificando visitas en sitio. Total documentos:', snapshot.size);

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    const visitanteNombre = data.visitanteNombre || data.visitante || 'Sin nombre';

                    // Verificar que esté dentro del fraccionamiento
                    const estadoAcceso = String(data.estadoAcceso || '').toLowerCase();

                    if (estadoAcceso === 'dentro') {
                        visitasEnSitioCount++;
                        console.log(`✅ [Administracion] ${visitanteNombre} contado - está dentro del fraccionamiento`);
                    } else {
                        console.log(`⏭️ [Administracion] Saltando ${visitanteNombre} - no está dentro (estado: ${estadoAcceso})`);
                    }
                });

                console.log(`📊 [Administracion] Total visitas en sitio (dentro): ${visitasEnSitioCount}`);
                setMetrics((prev) => ({
                    ...prev,
                    visitasHoy: visitasEnSitioCount,
                }));
            }
        );

        // Eventos próximos (próximos 7 días)
        const eventosUnsubscribe = onSnapshot(
            collection(db, 'fraccionamientos', fraccionamientoId, 'eventos'),
            (snapshot) => {
                let eventosCount = 0;
                const sevenDaysLater = new Date(today);
                sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    const fecha = data.fecha;
                    if (fecha) {
                        let fechaEvento = null;
                        if (typeof fecha.toDate === 'function') {
                            fechaEvento = fecha.toDate();
                        } else if (fecha instanceof Date) {
                            fechaEvento = fecha;
                        } else if (typeof fecha === 'string') {
                            fechaEvento = new Date(fecha);
                        }

                        if (fechaEvento && fechaEvento >= tomorrow && fechaEvento <= sevenDaysLater) {
                            eventosCount++;
                        }
                    }
                });

                setMetrics((prev) => ({
                    ...prev,
                    eventosProximos: eventosCount,
                }));
                setLoadingMetrics(false);
            }
        );

        return () => {
            casasUnsubscribe();
            ingresosUnsubscribe();
            visitasUnsubscribe();
            eventosUnsubscribe();
        };
    }, [fraccionamientoId]);

    // Cargar configuración de multas administrativas
    useEffect(() => {
        if (!fraccionamientoId) return;

        const loadMultasConfig = async () => {
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(fraccRef);

                if (snap.exists()) {
                    const data = snap.data();
                    const configFinanciera = data.configuracionFinanciera || {};
                    const multasEnabled = Boolean(configFinanciera.multasEnabled);
                    const multasList = Array.isArray(configFinanciera.multasAdministrativasList)
                        ? configFinanciera.multasAdministrativasList
                        : Array.isArray(configFinanciera.multasList)
                            ? configFinanciera.multasList
                            : [];

                    const cleanedMultas = multasList
                        .filter((m) => m && (m.descripcion || '').trim() && Number(m.monto || 0) > 0);

                    setMultasConfig({
                        enabled: multasEnabled,
                        list: cleanedMultas,
                    });
                }
            } catch (error) {
                console.error('Error cargando configuración de multas:', error);
            }
        };

        loadMultasConfig();
    }, [fraccionamientoId]);

    const gestionGeneral = [
        {
            nombre: 'Proveedores',
            path: '/comunidad/contacto-proveedores',
            icon: <FaHandshake />,
            descripcion: 'Gestiona contactos y servicios de proveedores'
        },
        {
            nombre: 'Eventos y Citas',
            path: '/comunidad/eventos',
            icon: <FaCalendarAlt />,
            descripcion: 'Crea y administra eventos y citas comunitarias'
        },
        {
            nombre: 'Anuncios',
            path: '/comunidad/avisos',
            icon: <FaBullhorn />,
            descripcion: 'Publica y gestiona anuncios para la comunidad'
        },
        {
            nombre: 'Reportes',
            path: '/comunidad/propuestas',
            icon: <FaChartLine />,
            descripcion: 'Revisa reportes e incidencias de residentes'
        },
    ];

    const gestionPagos = [
        {
            nombre: 'Consumo-Agua Potable',
            path: '/administracion/consumo-agua-potable',
            icon: <FaTint />,
            descripcion: 'Lectura y gestión de cobros por consumo variable de agua'
        },
        {
            nombre: 'Cuotas Extraordinarias',
            path: '/administracion/cuotas-extraordinarias',
            icon: <FaDonate />,
            descripcion: 'Visualiza y contabiliza el estado de cuotas extraordinarias'
        },
        {
            nombre: 'Desactivación RFID morosos',
            path: '/administracion/departamentos/rfid',
            icon: <FaBan />,
            descripcion: 'Gestiona accesos RFID de residentes morosos'
        },
        {
            nombre: 'Generar Recibo de Cobro',
            path: '/administracion/recibo-cobro',
            icon: <FaFileInvoiceDollar />,
            descripcion: 'Genera recibos de cobro para residentes morosos'
        },
        {
            nombre: 'Activar Convenios',
            path: '/administracion/convenios',
            icon: <FaHandshake />,
            descripcion: 'Activa convenios de pago para residentes morosos'
        },
    ];

    const departamentosLinks = [
        {
            nombre: 'Residentes',
            path: '/administracion/departamentos/residentes',
            icon: <FaUsers />,
            descripcion: 'Consulta y gestiona información de residentes'
        },
        {
            nombre: 'Identificación de Propietarios y Arrendados',
            path: '/administracion/departamentos/identificacion',
            icon: <FaIdCard />,
            descripcion: 'Identifica y registra propietarios y arrendados'
        },
        {
            nombre: 'Vigilancia',
            path: '/administracion/departamentos/vigilancia',
            icon: <FaShieldAlt />,
            descripcion: 'Exporta y revisa reportes de vigilancia'
        },
        {
            nombre: 'Constancia de No Adeudo',
            path: '/administracion/departamentos/constancia-no-adeudo',
            icon: <FaFileAlt />,
            descripcion: 'Genera constancias de no adeudo para residentes al corriente'
        },
    ];

    const accionesRapidas = [
        {
            nombre: 'Registrar Pago',
            path: '/admin/tickets',
            icon: <FaFileInvoiceDollar />,
            color: '#3b82f6'
        },
        {
            nombre: 'Nuevo Aviso/Votación',
            path: '/comunidad/avisos',
            icon: <FaBullhorn />,
            color: '#10b981'
        },
        {
            nombre: 'Ver Propuestas',
            path: '/comunidad/propuestas',
            icon: <FaChartLine />,
            color: '#f59e0b'
        },
        {
            nombre: 'Vigilancia/Reportes',
            path: '/administracion/departamentos/vigilancia',
            icon: <FaEye />,
            color: '#8b5cf6'
        },
    ];

    return (
        <div className="admin-page">
            <header className="admin-header">
                <div className="admin-header-content">
                    <div className="admin-header-icon">
                        <FaBuilding />
                    </div>
                    <div className="admin-header-text">
                        <h1>Administración</h1>
                        <p>Gestión administrativa del condominio</p>
                    </div>
                </div>
            </header>

            {/* Métricas rápidas */}
            <div className="admin-metrics-grid">
                <div className="admin-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#dbeafe' }}>
                        <FaUsers style={{ color: '#3b82f6' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{loadingMetrics ? '...' : metrics.totalResidentes}</div>
                        <div className="metric-label">Total Residentes</div>
                    </div>
                </div>

                <div className="admin-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#dcfce7' }}>
                        <FaChartLine style={{ color: '#10b981' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{loadingMetrics ? '...' : `${metrics.tasaCobro}%`}</div>
                        <div className="metric-label">Tasa de Cobro</div>
                    </div>
                </div>

                <div className="admin-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#fef3c7' }}>
                        <FaExclamationTriangle style={{ color: '#f59e0b' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{loadingMetrics ? '...' : metrics.pagosPendientes}</div>
                        <div className="metric-label">Pagos Pendientes</div>
                    </div>
                </div>

                <div className="admin-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#e9d5ff' }}>
                        <FaEye style={{ color: '#8b5cf6' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{loadingMetrics ? '...' : metrics.visitasHoy}</div>
                        <div className="metric-label">Visitas Hoy</div>
                    </div>
                </div>

                <div className="admin-metric-card">
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#fce7f3' }}>
                        <FaCalendarAlt style={{ color: '#ec4899' }} />
                    </div>
                    <div className="metric-content">
                        <div className="metric-value">{loadingMetrics ? '...' : metrics.eventosProximos}</div>
                        <div className="metric-label">Eventos Próximos</div>
                    </div>
                </div>
            </div>

            {/* Acciones rápidas */}
            <div className="admin-quick-actions">
                <h2 className="admin-section-title">Acciones Rápidas</h2>
                <div className="quick-actions-grid">
                    {accionesRapidas.map((accion, index) => (
                        <Link
                            key={index}
                            to={accion.path}
                            className="quick-action-btn"
                            style={{ '--action-color': accion.color }}
                        >
                            <div className="quick-action-icon" style={{ backgroundColor: accion.color }}>
                                {accion.icon}
                            </div>
                            <span>{accion.nombre}</span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Cards principales */}
            <div className="admin-grid">
                {/* --- Tarjeta 1: Gestión General --- */}
                <div className="admin-card admin-card-gestion">
                    <div className="admin-card-header" style={{ backgroundColor: '#3b82f6' }}>
                        <div className="admin-card-header-content">
                            <div className="admin-card-icon">
                                <FaBuilding />
                            </div>
                            <div className="admin-card-title-wrapper">
                                <h3>Gestión General</h3>
                                <p>Administra proveedores, eventos, anuncios y reportes</p>
                            </div>
                        </div>
                    </div>
                    <div className="admin-card-body">
                        <div className="admin-options-grid">
                            {gestionGeneral.map((item, index) => (
                                <Link key={index} to={item.path} className="admin-option-card">
                                    <div className="option-icon">{item.icon}</div>
                                    <div className="option-content">
                                        <h4>{item.nombre}</h4>
                                        <p>{item.descripcion}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- Tarjeta 2: Departamentos --- */}
                <div className="admin-card admin-card-departamentos">
                    <div className="admin-card-header" style={{ backgroundColor: '#10b981' }}>
                        <div className="admin-card-header-content">
                            <div className="admin-card-icon">
                                <FaUsers />
                            </div>
                            <div className="admin-card-title-wrapper">
                                <h3>Departamentos</h3>
                                <p>Gestión de residentes, identificación, vigilancia y Constancia No Adeudo</p>
                            </div>
                        </div>
                    </div>
                    <div className="admin-card-body">
                        <div className="admin-options-grid">
                            {departamentosLinks.map((item) => (
                                <Link key={item.path} to={item.path} className="admin-option-card">
                                    <div className="option-icon">{item.icon}</div>
                                    <div className="option-content">
                                        <h4>{item.nombre}</h4>
                                        <p>{item.descripcion}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- Tarjeta 3: Pagos y Servicios --- */}
                <div className="admin-card admin-card-pagos">
                    <div className="admin-card-header" style={{ backgroundColor: '#f59e0b' }}>
                        <div className="admin-card-header-content">
                            <div className="admin-card-icon">
                                <FaCreditCard />
                            </div>
                            <div className="admin-card-title-wrapper">
                                <h3>Pagos y Servicios</h3>
                                <p>Generación de tickets, gestión de accesos, Convenios y Sanciones</p>
                            </div>
                        </div>
                    </div>
                    <div className="admin-card-body">
                        <div className="admin-options-grid">
                            {gestionPagos
                                .filter(item => {
                                    if (item.nombre === 'Consumo-Agua Potable') {
                                        return modulos?.consumo_agua_potable !== false;
                                    }
                                    if (item.nombre === 'Desactivación RFID morosos') {
                                        return modulos?.desactivacion_rfid_morosos !== false;
                                    }
                                    return true;
                                })
                                .map((item, index) => (
                                    <Link key={index} to={item.path} className="admin-option-card">
                                        <div className="option-icon">{item.icon}</div>
                                        <div className="option-content">
                                            <h4>{item.nombre}</h4>
                                            <p>{item.descripcion}</p>
                                        </div>
                                    </Link>
                                ))
                            }
                            {/* Tarjeta de Cobro de Multas Administrativas */}
                            {multasConfig.enabled && multasConfig.list.length > 0 && (
                                <Link
                                    to="/administracion/cobro-sanciones"
                                    className="admin-option-card"
                                >
                                    <div className="option-icon"><FaGavel /></div>
                                    <div className="option-content">
                                        <h4>Cobro de Sanciones</h4>
                                        <p>Registra sanciones administrativas a residentes</p>
                                    </div>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Administracion;